import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { useScreenPlaylistAssignments } from './playlists/useScreenPlaylistAssignments';
import {
  buildMostPlayedPlaylists,
  buildReportScreenRows,
} from './reports/buildReportRows';
import { downloadReportCsv } from './reports/downloadReportCsv';
import { MostPlayedPlaylists } from './reports/MostPlayedPlaylists';
import { RangeChips } from './reports/RangeChips';
import { rangeBounds, type ReportRange } from './reports/reportRange';
import { ReportsScreenTable } from './reports/ReportsScreenTable';

/**
 * Reports — screen uptime / plays and most-played playlists
 * (Ghost Screen Reports.dc.html). Backed by live playback + screen data.
 */
export function ReportsPage() {
  const { t } = useTranslation('reports');
  const organisationId = useOrganisationId();
  const location = useLocation();
  const navigate = useNavigate();
  const [range, setRange] = useState<ReportRange>('7 days');
  const [toast, setToast] = useState<string | null>(null);
  const bounds = useMemo(() => rangeBounds(range), [range]);

  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    refetchInterval: 30_000,
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });
  const playsQuery = useQuery({
    queryKey: ['playback-plays', organisationId, bounds.start, bounds.end],
    queryFn: () =>
      api.listPlaybackEvents(organisationId, {
        ...bounds,
        limit: 2000,
      }),
  });
  const assignments = useScreenPlaylistAssignments(organisationId);

  const retry = useQueryRetry(async () => {
    await Promise.all([
      screensQuery.refetch(),
      locationsQuery.refetch(),
      playlistsQuery.refetch(),
      playsQuery.refetch(),
    ]);
  });

  const rows = useMemo(
    () =>
      buildReportScreenRows({
        range,
        screens: screensQuery.data ?? [],
        locations: locationsQuery.data ?? [],
        playlists: playlistsQuery.data ?? [],
        assignmentByScreenId: assignments.assignmentByScreenId,
        events: playsQuery.data ?? [],
      }),
    [
      range,
      screensQuery.data,
      locationsQuery.data,
      playlistsQuery.data,
      assignments.assignmentByScreenId,
      playsQuery.data,
    ],
  );

  const playlistBars = useMemo(
    () =>
      buildMostPlayedPlaylists({
        events: playsQuery.data ?? [],
        playlists: playlistsQuery.data ?? [],
      }),
    [playsQuery.data, playlistsQuery.data],
  );

  const loading =
    screensQuery.isPending ||
    locationsQuery.isPending ||
    playlistsQuery.isPending ||
    playsQuery.isPending ||
    assignments.isPending;
  const failed =
    screensQuery.isError ||
    locationsQuery.isError ||
    playlistsQuery.isError ||
    playsQuery.isError ||
    assignments.isError;

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (location.hash !== '#export' || loading) return;
    downloadReportCsv(rows, range);
    setToast(t('downloaded'));
    navigate({ pathname: '/reports', search: location.search }, { replace: true });
  }, [location.hash, location.search, navigate, rows, range, loading, t]);

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-[18px] self-stretch"
      style={{ animation: 'gs-in 260ms ease both', width: '100%' }}
    >
      <RangeChips value={range} onChange={setRange} />

      {failed || retry.isRetrying ? (
        <QueryErrorState
          message={t('loadError')}
          onRetry={retry.retry}
          isRetrying={retry.isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock />
      ) : (
        <>
          <ReportsScreenTable rows={rows} />
          <MostPlayedPlaylists bars={playlistBars} />
        </>
      )}

      {toast ? (
        <div
          className="fixed bottom-7 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-2.5 rounded-xl border px-[18px] py-3 text-sm font-semibold"
          style={{
            color: 'var(--gs-ink)',
            background: 'var(--gs-panel)',
            borderColor: 'var(--gs-line)',
            boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
            animation: 'gs-in 200ms ease both',
          }}
        >
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: 'var(--gs-ok)' }}
          />
          {toast}
        </div>
      ) : null}
    </div>
  );
}
